import { useState, useEffect, useMemo } from "react";
import { partyStructureSchemas, type partyStructureTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { EntitySelector } from "../components/EntitySelector";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { v7 as uuidv7 } from "uuid";
import type { Uuid } from "@pineseed/spec";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";

type Participant = partyStructureTypes["Participant"];
type ParticipantStatus = Participant['status'];
type View = 'participants' | 'stats';

const LAST_PARTICIPANT_VIEW_KEY = 'admin-last-participant-view';

export function ParticipantPanel() {
  const { adminApp, participant, group } = useAdmin();
  
  const [view, setView] = useState<View>(() => {
    const saved = localStorage.getItem(LAST_PARTICIPANT_VIEW_KEY) as View | null;
    return saved || 'participants';
  });

  // Persist view
  useEffect(() => {
    localStorage.setItem(LAST_PARTICIPANT_VIEW_KEY, view);
  }, [view]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<ParticipantStatus | ''>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  
  // Copy feedback
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Compute group options with member counts
  const groupOptions = useMemo(() => {
    return group.entities.map(g => ({
      id: g.id,
      label: g.name,
      metadata: `${(group.groupMembers[g.id] || []).length} members`,
    }));
  }, [group.entities, group.groupMembers]);

  // Apply filters
  useEffect(() => {
    if (statusFilter) {
      participant.filterByStatus(statusFilter);
    } else if (groupFilter) {
      participant.filterByGroup(groupFilter);
    } else {
      participant.loadEntities();
    }
  }, [statusFilter, groupFilter]);

  // CRUD handlers
  const handleCreate = async (data: Partial<Participant>) => {
    try {
      await participant.createEntity({
        ...data,
        id: uuidv7() as Uuid,
        workspaceId: adminApp.workspaceId as Uuid,
        // participantId: data.participantId || uuidv7() as ParticipantId, // Self-referencing
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create participant:', error);
      throw error;
    }
  };

  const handleEdit = async (data: Partial<Participant>) => {
    if (!editingParticipant) return;
    try {
      await participant.updateEntity(editingParticipant.id, data);
      setEditingParticipant(null);
    } catch (error) {
      console.error('Failed to update participant:', error);
      throw error;
    }
  };

  const handleDelete = async (p: Participant) => {
    if (!confirm(`Delete participant "${p.name}"?`)) return;
    try {
      await participant.deleteEntity(p.id);
    } catch (error) {
      console.error('Failed to delete participant:', error);
    }
  };

  // Quick status update
  const handleStatusUpdate = async (p: Participant, newStatus: ParticipantStatus) => {
    try {
      await participant.updateStatus(p.id, newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Get statistics
  const stats = participant.getStats();


  // Columns definition
  const participantColumns: Column<Participant>[] = [
    {
      key: 'id',
      label: 'ID / Name',
      sortable: true,
      render: (p) => (
        <div className="flex items-center gap-2">
          {p.avatar && (
            <img 
              src={p.avatar} 
              alt={p.name} 
              className="w-6 h-6 rounded-full object-cover"
            />
          )}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{p.name}</div>
            <CopyableText
              id={p.id}
              onCopy={copyToClipboard}
              isPulsing={isCopied(p.id)}
              variant="muted"
            >
              {p.id.slice(0, 8)}...{p.id.slice(-4)}
            </CopyableText>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type / Role',
      sortable: true,
      render: (p) => (
        <div className="flex flex-col gap-1">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 w-fit">
            {p.type}
          </span>
          {p.role && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 w-fit">
              {p.role}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (p) => {
        const statusColors: Record<string, string> = {
          online: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
          offline: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
          away: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
          busy: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
          typing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        };
        return (
          <div className="flex items-center gap-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[p.status] || 'bg-gray-100 text-gray-800'}`}>
              {p.status}
            </span>
            <select
              value={p.status}
              onChange={(e) => handleStatusUpdate(p, e.target.value as ParticipantStatus)}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="away">Away</option>
              <option value="busy">Busy</option>
              <option value="typing">Typing</option>
            </select>
          </div>
        );
      },
    },
    {
      key: 'groupId',
      label: 'Group',
      render: (p) => p.groupId ? (
        <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
          {p.groupId.slice(0, 6)}...
        </span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      sortable: true,
      render: (p) => p.lastSeen ? (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {new Date(p.lastSeen).toLocaleString()}
        </span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (p) => (
        <span className="text-xs text-gray-500">{new Date(p.createdAt).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Participant Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage participants across the workspace
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create Participant
        </button>
      </div>

      {/* View Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {(['participants', 'stats'] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 border-b-2 transition-colors ${
                view === v
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
              {v === 'participants' && <span className="ml-2 text-xs">({stats.total})</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      {view === 'participants' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as ParticipantStatus | '');
                  setGroupFilter('');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="away">Away</option>
                <option value="busy">Busy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group
              </label>
              <EntitySelector
                entityType="group"
                value={groupFilter}
                onChange={(value) => {
                  setGroupFilter(value);
                  setStatusFilter('');
                }}
                placeholder="All Groups"
                customOptions={groupOptions}
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={() => participant.refresh()}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Refresh
              </button>
              {(statusFilter || groupFilter) && (
                <button
                  onClick={() => {
                    setStatusFilter('');
                    setGroupFilter('');
                    participant.clearFilters();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats View */}
      {view === 'stats' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Overview
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Participants:</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{stats.total}</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              By Status
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.byStatus).map(([status, count]) => (
                <div key={status} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {status}:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              By Type
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.byType).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {type}:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Participants Table */}
      {view === 'participants' && (
        <EntityTable
          data={participant.entities}
          columns={participantColumns}
          onEdit={setEditingParticipant}
          onDelete={handleDelete}
          loading={participant.loading}
          emptyMessage="No participants found. Create your first participant to get started."
        />
      )}

      {/* Create Participant Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Participant"
      >
        <EntityForm
          schema={partyStructureSchemas.CreateParticipantRequest}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
          submitLabel="Create"
          customOptions={{ group: groupOptions }}
        />
      </Modal>

      {/* Edit Participant Modal */}
      <Modal
        isOpen={!!editingParticipant}
        onClose={() => setEditingParticipant(null)}
        title="Edit Participant"
      >
        {editingParticipant && (
          <EntityForm
            schema={partyStructureSchemas.UpdateParticipantRequest}
            initialData={editingParticipant}
            onSubmit={handleEdit}
            onCancel={() => setEditingParticipant(null)}
            submitLabel="Update"
            customOptions={{ group: groupOptions }}
          />
        )}
      </Modal>
    </div>
  );
}

